import { Button } from '@jouzen/ecom-components/v3/Button';
import { GridItem } from '@jouzen/ecom-components/v3/GridItem';
import { LayoutGrid } from '@jouzen/ecom-components/v3/LayoutGrid';
import { Typography } from '@jouzen/ecom-components/v3/Typography';
import { AnimatePresence } from 'framer-motion';
import { useTranslations } from 'next-intl';
import { useRef } from 'react';
import type { MessageKey } from 'types/LocaleMessageKey';

import Image from '@/app/components/Image';
import Motion from '@/app/components/Motion';

import { useScrollActiveTab } from './hooks/useScrollActiveTab';
import LandingPageScrollFeatureImages from './LandingPageScrollFeatureImages';
import type { LandingPageScrollFeatureTabProps } from './LandingPageScrollFeatureTabs';
import LandingPageScrollFeatureTabs from './LandingPageScrollFeatureTabs';
import LandingPageScrollFeatureTitle from './LandingPageScrollFeatureTitle';

interface LandingPageScrollFeaturesProps {
  readonly title?: MessageKey | undefined;
  readonly eyebrow?: MessageKey | undefined;
  readonly tabs: LandingPageScrollFeatureTabProps[];
  readonly flip?: boolean | undefined;
}

const LandingPageScrollFeatures = ({
  title,
  eyebrow,
  tabs,
  flip = false,
}: LandingPageScrollFeaturesProps) => {
  const divRef = useRef<HTMLDivElement>(null);
  const t = useTranslations();
  const activeTab = useScrollActiveTab(divRef, tabs.length);

  return (
    <section data-cy="landing-page-scroll-features">
      <div
        ref={divRef}
        className="relative hidden lg:block"
        style={{ height: `calc(100vh + 500px * ${tabs.length})` }}
      >
        <div className="sticky top-0 h-screen">
          <AnimatePresence>
            <Motion
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 flex items-center justify-center"
            >
              <LayoutGrid>
                <GridItem
                  className="mb-0 flex flex-col justify-center"
                  colStart={{ sm: 'main', lg: 'main' }}
                  colEnd={{ sm: 4, lg: 10 }}
                >
                  {flip ? (
                    <LandingPageScrollFeatureTabs
                      activeTab={activeTab}
                      tabs={tabs}
                    />
                  ) : (
                    <>
                      {title && (
                        <LandingPageScrollFeatureTitle content={title} />
                      )}
                    </>
                  )}
                </GridItem>
                <GridItem
                  colStart={{ sm: 'main', lg: 10 }}
                  colEnd={{ sm: 4, lg: 16 }}
                >
                  <LandingPageScrollFeatureImages
                    tabs={tabs}
                    activeTab={activeTab}
                  />
                </GridItem>
                <GridItem
                  className="flex flex-col justify-center"
                  colStart={{ sm: 'main', lg: 16 }}
                  colEnd={{ sm: 4, lg: 'main' }}
                >
                  {flip ? (
                    <>
                      {title && (
                        <LandingPageScrollFeatureTitle content={title} />
                      )}
                    </>
                  ) : (
                    <LandingPageScrollFeatureTabs
                      activeTab={activeTab}
                      tabs={tabs}
                    />
                  )}
                </GridItem>
              </LayoutGrid>
            </Motion>
          </AnimatePresence>
        </div>
      </div>
      <div className="block lg:hidden">
        <LayoutGrid>
          <GridItem colStart={{ sm: 'main' }} colEnd={{ sm: 'main' }}>
            {title && (
              <div className="pt-16 pb-8">
                <LandingPageScrollFeatureTitle content={title} />
              </div>
            )}
            {tabs.map((tab) => (
              <div key={tab.title}>
                <div className="flex flex-row justify-center">
                  <Image
                    src={tab.image}
                    localizedAlt={tab.imageAlt}
                    width={417}
                    height={674}
                  />
                </div>
                {eyebrow && (
                  <Typography className="uppercase">{t(eyebrow)}</Typography>
                )}
                <Typography
                  className="font-normal tracking-tighter"
                  color="inherit"
                  Element="h3"
                  variant="h4"
                >
                  {t(tab.title)}
                </Typography>
                <Motion
                  animate={{
                    opacity: 1,
                  }}
                  className="overflow-hidden"
                  transition={{ duration: 0.2, ease: 'easeInOut' }}
                >
                  <Typography
                    Element="p"
                    color="inherit"
                    className="mt-4 text-body-sm"
                  >
                    {t(tab.copy)}
                  </Typography>
                  {tab.actionLabel && (
                    <Button
                      className="my-4 mb-8"
                      variant="secondary-dark"
                      size="small"
                      onClick={tab.actionOnClick}
                    >
                      {t(tab.actionLabel)}
                    </Button>
                  )}
                </Motion>
              </div>
            ))}
          </GridItem>
        </LayoutGrid>
      </div>
    </section>
  );
};

export default LandingPageScrollFeatures;
